import { Release, ReleaseGetResponse, ReleaseSnapshot, ReleaseStatus } from '@fsp/types';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useMatches } from 'react-router-dom';
import { fetchRelease, fetchReleases } from '../services/release';
import { useAuth } from './AuthContext';

export type ViewMode = 'edit' | 'latest';

interface ReleasesContextType {
  // List of all releases
  releases: Release[];
  setReleases: (releases: Release[]) => void;
  refreshReleases: () => Promise<void>;
  isLoadingReleases: boolean;

  // Current release detail (with chapters)
  currentRelease: Release | null;
  currentReleaseChapters: ReleaseSnapshot[];
  refreshCurrentRelease: () => Promise<void>;
  isLoadingRelease: boolean;

  // Latest approved release
  latestRelease: Release | null;
  latestReleaseChapters: ReleaseSnapshot[];
  isLoadingLatestRelease: boolean;

  // View mode (edit/new or latest)
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Helper to get chapter from current release
  getChapter: (chapterId: string) => ReleaseSnapshot | undefined;
}

const ReleasesContext = createContext<ReleasesContextType | undefined>(undefined);

export const ReleasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { planId, releaseId } = useParams<{ planId: string; releaseId?: string }>();
  const { organisationId } = useAuth();
  const matches = useMatches();

  // State for releases list
  const [releases, setReleases] = useState<Release[]>([]);
  const [isLoadingReleases, setIsLoadingReleases] = useState(false);

  // State for current release detail
  const [currentRelease, setCurrentRelease] = useState<Release | null>(null);
  const [currentReleaseChapters, setCurrentReleaseChapters] = useState<ReleaseSnapshot[]>([]);
  const [isLoadingRelease, setIsLoadingRelease] = useState(false);

  // State for latest approved release
  const [latestRelease, setLatestRelease] = useState<Release | null>(null);
  const [latestReleaseChapters, setLatestReleaseChapters] = useState<ReleaseSnapshot[]>([]);
  const [isLoadingLatestRelease, setIsLoadingLatestRelease] = useState(false);

  // Determine view mode from route metadata
  // Find the last route match that has a viewMode handle
  const currentMatch = [...matches].reverse().find((match) => {
    const handle = match.handle as { viewMode?: ViewMode } | undefined;
    return handle?.viewMode;
  });
  const routeViewMode = (currentMatch?.handle as { viewMode?: ViewMode } | undefined)?.viewMode;

  const [viewMode, setViewMode] = useState<ViewMode>(routeViewMode || 'edit');

  // Fetch releases list
  const refreshReleases = useCallback(async () => {
    if (!planId || !organisationId) {
      setReleases([]);
      return;
    }

    setIsLoadingReleases(true);
    try {
      const releasesList = await fetchReleases(organisationId, planId);
      setReleases(releasesList);

      // Find and fetch the latest approved release
      const latest = releasesList
        .filter((r) => r.status === ReleaseStatus.APPROVED)
        .sort(
          (a, b) => new Date(b.releasedAt || 0).getTime() - new Date(a.releasedAt || 0).getTime()
        )[0];

      if (latest) {
        setIsLoadingLatestRelease(true);
        try {
          const data: ReleaseGetResponse = await fetchRelease(organisationId, planId, latest.id);
          setLatestRelease(data.release);
          setLatestReleaseChapters(data.chapters);
        } catch (error) {
          console.error('Failed to fetch latest release:', error);
          setLatestRelease(null);
          setLatestReleaseChapters([]);
        } finally {
          setIsLoadingLatestRelease(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch releases:', error);
      setReleases([]);
    } finally {
      setIsLoadingReleases(false);
    }
  }, [organisationId, planId]);

  // Fetch current release detail
  const refreshCurrentRelease = useCallback(async () => {
    if (!planId || !organisationId || !releaseId) {
      setCurrentRelease(null);
      setCurrentReleaseChapters([]);
      return;
    }

    setIsLoadingRelease(true);
    try {
      const data: ReleaseGetResponse = await fetchRelease(organisationId, planId, releaseId);
      setCurrentRelease(data.release);
      setCurrentReleaseChapters(data.chapters);
    } catch (error) {
      console.error('Failed to fetch release:', error);
      setCurrentRelease(null);
      setCurrentReleaseChapters([]);
    } finally {
      setIsLoadingRelease(false);
    }
  }, [organisationId, planId, releaseId]);

  // Helper to get a specific chapter
  const getChapter = useCallback(
    (chapterId: string): ReleaseSnapshot | undefined => {
      return currentReleaseChapters.find((ch) => ch.chapterId === chapterId);
    },
    [currentReleaseChapters]
  );

  // Auto-fetch releases when planId changes
  useEffect(() => {
    if (planId && organisationId) {
      refreshReleases();
    } else {
      setReleases([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, organisationId]);

  // Auto-fetch current release when releaseId changes
  useEffect(() => {
    if (planId && organisationId && releaseId) {
      refreshCurrentRelease();
    } else {
      setCurrentRelease(null);
      setCurrentReleaseChapters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, organisationId, releaseId]);

  // Update view mode when route changes
  useEffect(() => {
    if (routeViewMode) {
      setViewMode(routeViewMode);
    }
  }, [routeViewMode]);

  return (
    <ReleasesContext.Provider
      value={{
        releases,
        setReleases,
        refreshReleases,
        isLoadingReleases,
        currentRelease,
        currentReleaseChapters,
        refreshCurrentRelease,
        isLoadingRelease,
        latestRelease,
        latestReleaseChapters,
        isLoadingLatestRelease,
        viewMode,
        setViewMode,
        getChapter,
      }}
    >
      {children}
    </ReleasesContext.Provider>
  );
};

export const useReleases = (): ReleasesContextType => {
  const context = useContext(ReleasesContext);
  if (!context) {
    throw new Error('useReleases must be used within a ReleasesProvider');
  }
  return context;
};
